import React from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { handleUnauthorizedResponse } from '../../../api/http';
import { AuthData } from '../../../context/auth';
import {
	RenewalData,
	withRenewalContext,
} from '../../../context/RenewalProvider';
import { ExposureCreateRequest, Peril } from '../../../types/policy';
import {
	StyledSectionContainer,
	StyledTitle,
	StyledTitleContainer,
} from '../../common';
import { errorToast, successToast } from '../../Toast';
import ConfirmationModal from '../ConfirmationModal';
import AddPerilModal from './AddRenewalPerilModal';
import PerilsList from './PerilsList';

interface Props {
	authData: AuthData;
	renewalData: RenewalData;
}

/**
 * The peril list for an added exposure on an renewal.
 * This list can only contain added perils.
 */
const AddedExposurePerils = (props: Props) => {
	const { t } = useTranslation();
	const { getValues, formState, reset } = useFormContext();
	const { dirtyFields } = formState;
	const [isConfirmModalOpen, setIsConfirmModalOpen] = React.useState(false);
	const [removedPeril, setRemovePeril] = React.useState(null);
	const params = useParams();
	const { exposureId } = params;
	const addedExposureIndex = parseInt(exposureId.split('-')[1], 10);

	const { renewalData, authData } = props;

	const { productConfig, renewalUpdateObj, setLoading, saveFastForm } =
		renewalData;
	const selectedExposure =
		renewalData.selectedExposure as ExposureCreateRequest;
	const isInEditMode =
		authData.hasPermission('renewal.update') &&
		renewalUpdateObj.state === 'application';

	if (!selectedExposure) {
		return null;
	}

	const removePeril = async () => {
		setIsConfirmModalOpen(false);
		setLoading(true);
		try {
			await saveFastForm(getValues(), dirtyFields, reset, {
				removeAddedPeril: {
					perilLocator: removedPeril.locator,
					exposureLocator: selectedExposure.locator,
					exposureIsAdded: true,
					exposureIndex: addedExposureIndex,
				},
			});
			successToast('Successfully removed peril');
		} catch (error) {
			errorToast('Error removing peril');
			if (await handleUnauthorizedResponse(error, authData)) {
				return;
			}
		} finally {
			setLoading(false);
		}
	};

	const policyConfig = productConfig.policyConfiguration;
	const exposureConfig = policyConfig.exposures.find(
		(e) => e.name === selectedExposure.exposureName,
	);
	const perilConfigs = exposureConfig.perils;

	const renderActions = () => {
		return (
			<AddPerilModal
				perilConfigs={perilConfigs}
				exposureLocator={selectedExposure.exposureLocator}
			/>
		);
	};

	const renderRemovePerilModal = () => {
		return (
			<ConfirmationModal
				entityName={removedPeril.name}
				entityType={t('peril.singular.lower')}
				authToken={authData.auth.authorizationToken}
				onRemove={removePeril}
				cancel={() => setIsConfirmModalOpen(false)}
			/>
		);
	};

	const openConfirmationModal = (peril: Peril) => {
		setIsConfirmModalOpen(true);
		setRemovePeril(peril);
	};

	return (
		<>
			{isConfirmModalOpen && renderRemovePerilModal()}
			<StyledTitleContainer>
				<StyledTitle>{t('peril.plural.upper')}</StyledTitle>
			</StyledTitleContainer>
			<hr />
			<StyledSectionContainer>
				<PerilsList
					perils={selectedExposure.perils}
					openConfirmationModal={(peril: Peril) => openConfirmationModal(peril)}
				/>
			</StyledSectionContainer>
			{isInEditMode && renderActions()}
		</>
	);
};

export default withRenewalContext<Props>(AddedExposurePerils);
